import { ticketAndBadgesUserTypes } from "@ac-expo/api-types";
import { applyDiscount, applyTimeToDate, isNullish } from "@ac-expo/utils";
import { Time } from "@internationalized/date";
import { DiscountType, TicketUserType } from "@prisma/client";
import { isBefore } from "date-fns";
import { z } from "zod";
import {
  addDuplicatedDiscountCodeIssues,
  createDegressivePriceSchema,
  createDiscountCodeSchema,
} from "../discount-code";
import { fileSchema } from "../files";
import { CustomAppearanceSchema, CustomPdfAppearanceSchema } from "../utils";

export const createPriceSchema = z.object({
  id: z.string().optional(),
  priceTTC: z.coerce.number().gt(0, "Le prix doit être supérieur à 0"),
  vatRate: z.union([
    z.coerce
      .number()
      .gte(0, "Le taux doit être supérieur à 0%")
      .lte(100, "Le taux ne peux pas dépasser 100%"),
    z.string().min(1, "Le taux doit être renseigné"),
  ]),
});

export const defaultCreatePriceValues = {
  priceTTC: 0,
  vatRate: "0",
};

export type CreatePrice = z.infer<typeof createPriceSchema>;

export const DUPLICATED_DISCOUNT_CODE_IN_TICKET_MESSAGE =
  "Ce nom est déjà utilisé par un autre code promo de ce billet";

export const createTicketTypeSchema = z
  .object({
    id: z.string().optional(),
    name: z
      .string()
      .min(1, "Le nom du ticket est requis")
      .max(128, "Le nom du ticket est trop long"),
    quantity: z.coerce
      .number({ invalid_type_error: "La quantité est nécessaire" })
      .int()
      .or(z.literal("Infini")),

    validityDates: z
      .array(
        z
          .date({
            required_error: "La date de validité est requise",
          })
          .refine((date) => !isNaN(date.getTime()), "Date de validité invalide"),
      )
      .min(1, "Les dates de validité sont requises"),
    isSingleEntry: z.boolean(),
    numberEntriesPerDay: z.coerce.number().int().or(z.literal("Infini")).nullish(),
    isInfiniteEntries: z.boolean(),
    userType: z.enum(ticketAndBadgesUserTypes as [TicketUserType, ...TicketUserType[]]),

    buyingLimitForUserType: z.coerce
      .number()
      .gt(0, "Le nombre de billets par utilisateur doit être supérieur à 0")
      .nullable(),

    isPaid: z.boolean(),
    price: createPriceSchema.nullish(),

    groupOnly: z.boolean(),
    groupMinSize: z.coerce.number().int().gt(1, "Les prix de groupes sont à partir de 2").nullish(),

    isDegressivePrice: z.boolean(),
    degressivePrices: z.array(createDegressivePriceSchema).superRefine((dps, ctx) =>
      dps.forEach((a, i) => {
        dps.forEach((b, j) => {
          if (i === j) return;
          const aMin = a.minQuantity ?? 0;
          const aMax = a.maxQuantity ?? Infinity;
          const bMin = b.minQuantity ?? 0;
          const bMax = b.maxQuantity ?? Infinity;

          if (aMax >= bMin && aMin <= bMax) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Les plages de quantités ne doivent pas se chevaucher",
              path: [i, aMin <= bMin ? "maxQuantity" : "minQuantity"],
            });
          }
        });
      }),
    ),

    areDiscountsCodesEnabled: z.boolean(),
    discountCodes: z.array(createDiscountCodeSchema),
    activityId: z.string().optional(),
    hasCustomAppearance: z.boolean().default(false),
    customAppearance: CustomAppearanceSchema.nullable(),
    customPdfAppearance: CustomPdfAppearanceSchema.nullable(),
    templatePdf: fileSchema.nullable(),
    eventGates: z.array(z.string()),
    isAvailableOnCashRegister: z.boolean(),
    isAvailableOnOnlineTicketing: z.boolean(),
    saleStartDate: z.date().nullable(),
    saleStartTime: z.instanceof(Time).nullish(),
    saleEndDate: z.date().nullable(),
    saleEndTime: z.instanceof(Time).nullish(),
    hasEventGates: z.boolean().default(false),
    hasAlertThreshold: z.boolean().default(false),
    alertThreshold: z.coerce.number().nullable().optional(),
    hasGroupVisibilityAndBuyingLimits: z.boolean().default(false),
    groupLimits: z
      .array(
        z.object({
          groupIds: z.array(z.string()).min(1, "Veuillez sélectionner un groupe"),
          maxTicketsPerUser: z.coerce
            .number()
            .gt(0, "Le nombre de billets par utilisateur doit être supérieur à 0")
            .nullable(),
        }),
      )
      .nullable(),
  })
  .refine(({ groupOnly, groupMinSize }) => !groupOnly || !isNullish(groupMinSize), {
    message: "La taille minimale du groupe est requise",
    path: ["groupMinSize"],
  })
  .refine(
    ({ isDegressivePrice, degressivePrices }) => !isDegressivePrice || degressivePrices.length > 0,
    {
      message: "Au moins un prix dégressif est requis",
      path: ["degressivePrices"],
    },
  )
  .refine(
    ({ areDiscountsCodesEnabled, discountCodes }) =>
      !areDiscountsCodesEnabled || discountCodes.length > 0,
    {
      message: "Au moins un code de réduction est requis",
      path: ["discountCodes"],
    },
  )
  .refine(
    ({ isSingleEntry, numberEntriesPerDay }) =>
      (isSingleEntry && !numberEntriesPerDay) || (!isSingleEntry && numberEntriesPerDay),
    {
      message: "Le nombre d'entrées par jour est requis",
      path: ["numberEntriesPerDay"],
    },
  )
  .superRefine(({ isPaid, groupOnly, isDegressivePrice, areDiscountsCodesEnabled }, ctx) => {
    if (isPaid) return;
    if (groupOnly)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Le prix des tickets est nécessaires.",
        path: ["groupOnly"],
      });
    if (isDegressivePrice)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Le prix des tickets est nécessaire",
        path: ["isDegressivePrice"],
      });
    if (areDiscountsCodesEnabled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Le prix des tickets est nécessaire",
        path: ["areDiscountsCodesEnabled"],
      });
    }
  })
  .superRefine(({ discountCodes }, ctx) =>
    addDuplicatedDiscountCodeIssues(discountCodes, ctx, DUPLICATED_DISCOUNT_CODE_IN_TICKET_MESSAGE),
  )
  .superRefine(({ isPaid, price, degressivePrices, discountCodes }, ctx) => {
    if (!isPaid || !price) return;
    degressivePrices?.forEach((dp, i) => {
      if (dp.discount.type === DiscountType.euro && dp.discount.value > price.priceTTC) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La réduction ne peut pas être supérieure au prix du ticket",
          path: ["degressivePrices", i, "discount", "value"],
        });
      }
    });
    discountCodes?.forEach((dc, i) => {
      if (dc.discount.type === DiscountType.euro && dc.discount.value > price.priceTTC) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La réduction ne peut pas être supérieure au prix du ticket",
          path: ["discountCodes", i, "discount", "value"],
        });
      }
    });
  })
  .superRefine(({ degressivePrices, price, isPaid }, ctx) => {
    if (!isPaid || !price) return;
    const compareByApplyingDiscount = (
      a: (typeof degressivePrices)[number],
      b: (typeof degressivePrices)[number],
    ) => {
      return (
        applyDiscount(price?.priceTTC, a.discount) - applyDiscount(price?.priceTTC, b.discount)
      );
    };
    const sortedDegressivePrices = degressivePrices
      .map((v, i) => ({ ...v, index: i }))
      .sort(compareByApplyingDiscount);
    sortedDegressivePrices.forEach((dp, i) => {
      for (let j = i + 1; j < sortedDegressivePrices.length; j++) {
        const otherDp = sortedDegressivePrices[j];
        if (!otherDp.maxQuantity || (dp.minQuantity ?? 0) < otherDp.maxQuantity) {
          const errorMessage =
            "Cette réduction est plus avantageuse qu'une autre pouvant s'appliquer à plus de billets";
          const errorPath = ["degressivePrices", dp.index];

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMessage,
            path: [...errorPath, "minQuantity"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMessage,
            path: [...errorPath, "maxQuantity"],
          });
        }
      }
    });
  })
  .refine(
    ({ hasGroupVisibilityAndBuyingLimits, groupLimits }) =>
      !hasGroupVisibilityAndBuyingLimits ||
      (hasGroupVisibilityAndBuyingLimits && groupLimits && groupLimits.length > 0),
    {
      message: "Veuillez renseigner au moins une limite",
      path: ["groupLimits"],
    },
  )
  .superRefine((data) => {
    if (data.numberEntriesPerDay === "Infini") {
      data.isInfiniteEntries = true;
    } else if (data.isInfiniteEntries) {
      data.numberEntriesPerDay = null;
    }
  })
  .refine(
    ({ hasAlertThreshold, alertThreshold }) =>
      !hasAlertThreshold || (alertThreshold && alertThreshold >= 1),
    {
      message: "Le seuil d'alerte doit être défini et supérieur ou égal à 1",
      path: ["alertThreshold"],
    },
  )
  .refine(
    ({ hasGroupVisibilityAndBuyingLimits, groupLimits }) =>
      !hasGroupVisibilityAndBuyingLimits ||
      (hasGroupVisibilityAndBuyingLimits && groupLimits && groupLimits.length > 0),
    {
      message: "Veuillez renseigner au moins une limite",
      path: ["groupLimits"],
    },
  )
  .refine(
    ({ saleStartDate, saleStartTime }) =>
      (saleStartDate && saleStartTime) || (!saleStartDate && !saleStartTime),
    {
      message: "La date et l'heure doivent être renseignées ensemble",
      path: ["saleStartTime"],
    },
  )
  .refine(
    ({ saleEndDate, saleEndTime }) =>
      (saleEndDate && saleEndTime) || (!saleEndDate && !saleEndTime),
    {
      message: "La date et l'heure doivent être renseignées ensemble",
      path: ["saleEndTime"],
    },
  )
  .refine(
    ({ saleStartDate, saleEndDate, saleStartTime, saleEndTime }) =>
      !saleStartDate ||
      !saleStartTime ||
      !saleEndDate ||
      !saleEndTime ||
      isBefore(
        applyTimeToDate(saleStartDate, saleStartTime),
        applyTimeToDate(saleEndDate, saleEndTime),
      ),
    {
      message: "La date de début de mise en vente doit être antérieure à la date de fin",
      path: ["saleStartDate"],
    },
  );

export const defaultCreateTicketTypeValues = {
  name: "",
  quantity: "Infini" as const,
  validityDates: [],
  isSingleEntry: true,
  numberEntriesPerDay: undefined,
  userType: TicketUserType.all,
  buyingLimitForUserType: null,
  isPaid: false,
  price: null,
  groupOnly: false,
  groupMinSize: null,
  isDegressivePrice: false,
  degressivePrices: [],
  areDiscountsCodesEnabled: false,
  discountCodes: [],
  activityId: undefined,
  isInfiniteEntries: false,
  hasCustomAppearance: false,
  customAppearance: null,
  customPdfAppearance: null,
  templatePdf: null,
  isAvailableOnCashRegister: false,
  isAvailableOnOnlineTicketing: true,
  saleStartDate: null,
  saleStartTime: null,
  saleEndDate: null,
  saleEndTime: null,
  hasGroupVisibilityAndBuyingLimits: false,
  groupLimits: null,
  eventGates: [],
  hasEventGates: false,
  hasAlertThreshold: false,
  alertThreshold: undefined,
};

export type CreateTicketTypeForm = z.infer<typeof createTicketTypeSchema>;
